import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

const BACKGROUND_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const CACHE_CLEAR_THRESHOLD = 60 * 1000; // 1 minute

/**
 * Enhanced lifecycle hook for iOS PWA crash prevention
 * Handles backgrounding, pausing media, and crash recovery
 */
export const useAppLifecycle = () => {
  const [isAppVisible, setIsAppVisible] = useState(!document.hidden);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const backgroundTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hiddenAtRef = useRef<number | null>(null);
  const memoryWarningRef = useRef<boolean>(false);

  useEffect(() => {
    // Enhanced media management - unload sources to free memory (iOS crash prevention)
    const pauseAllMedia = () => {
      try {
        const videos = document.querySelectorAll('video');
        const audios = document.querySelectorAll('audio');
        
        videos.forEach((video, index) => {
          if (!video.paused) {
            video.pause();
            video.dataset.wasPlaying = 'true';
          }
          // Aggressive memory cleanup - unload video sources
          const src = video.currentSrc || video.src;
          if (src) {
            sessionStorage.setItem(`video_src_${index}`, src);
            video.removeAttribute('src');
            video.load(); // This frees memory
            video.dataset.unloaded = 'true';
          }
        });
        
        audios.forEach((audio, index) => {
          if (!audio.paused) {
            audio.pause();
            audio.dataset.wasPlaying = 'true';
          }
          // Aggressive memory cleanup - unload audio sources
          const src = audio.currentSrc || audio.src;
          if (src) {
            sessionStorage.setItem(`audio_src_${index}`, src);
            audio.removeAttribute('src');
            audio.load(); // This frees memory
            audio.dataset.unloaded = 'true';
          }
        });
      } catch (error) {
        console.error('Error pausing media:', error);
      }
    };

    // Resume media that was playing before backgrounding
    const resumeMedia = () => {
      try {
        const videos = document.querySelectorAll('video');
        const audios = document.querySelectorAll('audio');
        
        videos.forEach((video, index) => {
          // Reload unloaded video sources
          if (video.dataset.unloaded === 'true') {
            const src = sessionStorage.getItem(`video_src_${index}`);
            if (src) {
              video.src = src;
              video.load();
              sessionStorage.removeItem(`video_src_${index}`);
            }
            delete video.dataset.unloaded;
          }
          
          if (video.dataset.wasPlaying === 'true') {
            video.play().catch(e => console.log('Could not resume video:', e));
            delete video.dataset.wasPlaying;
          }
        });
        
        audios.forEach((audio, index) => {
          // Reload unloaded audio sources
          if (audio.dataset.unloaded === 'true') {
            const src = sessionStorage.getItem(`audio_src_${index}`);
            if (src) {
              audio.src = src;
              audio.load();
              sessionStorage.removeItem(`audio_src_${index}`);
            }
            delete audio.dataset.unloaded;
          }
          
          if (audio.dataset.wasPlaying === 'true') {
            audio.play().catch(e => console.log('Could not resume audio:', e));
            delete audio.dataset.wasPlaying;
          }
        });
      } catch (error) {
        console.error('Error resuming media:', error);
      }
    };

    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      setIsAppVisible(isVisible);

      if (!isVisible) {
        // App going to background
        console.log('App backgrounding...');
        hiddenAtRef.current = Date.now();
        
        // Pause and unload all media to prevent iOS crashes
        pauseAllMedia();
        
        // Clear non-essential React Query cache after threshold
        setTimeout(() => {
          if (document.hidden) {
            console.log('Clearing non-essential cache...');
            queryClient.removeQueries({ 
              predicate: (query) => {
                const key = query.queryKey[0] as string;
                return key?.includes('book-media') || 
                       key?.includes('covers') || 
                       key?.includes('images');
              }
            });
          }
        }, CACHE_CLEAR_THRESHOLD);
        
        // Save current path for recovery
        try {
          sessionStorage.setItem('lastPath', window.location.pathname);
          sessionStorage.setItem('backgroundTime', Date.now().toString());
        } catch (error) {
          console.error('Error saving state:', error);
        }
        
        // Clear any existing timer
        if (backgroundTimerRef.current) {
          clearTimeout(backgroundTimerRef.current);
        }

        // Set timeout to redirect after 10 minutes
        backgroundTimerRef.current = setTimeout(() => {
          if (document.hidden) {
            console.log('App backgrounded for 10+ minutes');
          }
        }, BACKGROUND_TIMEOUT_MS);

      } else {
        // App coming to foreground
        console.log('App foregrounding...');
        
        const wasHiddenFor = hiddenAtRef.current 
          ? Date.now() - hiddenAtRef.current 
          : 0;

        // Clear timer
        if (backgroundTimerRef.current) {
          clearTimeout(backgroundTimerRef.current);
          backgroundTimerRef.current = null;
        }

        // Refresh critical queries to prevent stale data/wrong UI
        console.log('Refreshing critical app data...');
        queryClient.invalidateQueries({ queryKey: ['appConfig'] });
        queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
        queryClient.refetchQueries({ 
          queryKey: ['appConfig'],
          type: 'active'
        });
        queryClient.refetchQueries({ 
          queryKey: ['subscriptionStatus'],
          type: 'active'
        });

        // Resume media playback
        setTimeout(() => {
          resumeMedia();
        }, 100);

        // If was hidden for more than 10 minutes, clear profile selection to force re-selection
        // The AppFlowOverlay will show the profile selector automatically
        if (wasHiddenFor > BACKGROUND_TIMEOUT_MS) {
          console.log('Clearing profile selection after long background period');
          sessionStorage.removeItem('profile-selection-shown');
          // Trigger a re-render by navigating to the current page
          window.location.reload();
        }

        hiddenAtRef.current = null;
      }
    };

    // Handle page freeze/resume for iOS
    const handleFreeze = () => {
      console.log('Page frozen (iOS background)');
      pauseAllMedia();
    };

    const handleResume = () => {
      console.log('Page resumed (iOS foreground)');
      setTimeout(() => {
        resumeMedia();
      }, 100);
    };

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // iOS-specific events for PWA lifecycle
    document.addEventListener('freeze', handleFreeze);
    document.addEventListener('resume', handleResume);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('freeze', handleFreeze);
      document.removeEventListener('resume', handleResume);
      
      if (backgroundTimerRef.current) {
        clearTimeout(backgroundTimerRef.current);
      }
    };
  }, [navigate, queryClient]);

  // Monitor memory warnings (iOS optimization)
  useEffect(() => {
    const checkMemoryPressure = () => {
      // @ts-ignore - performance.memory is available in some browsers
      if (performance.memory) {
        // @ts-ignore
        const { usedJSHeapSize, jsHeapSizeLimit } = performance.memory;
        const memoryUsageRatio = usedJSHeapSize / jsHeapSizeLimit;
        
        if (memoryUsageRatio > 0.9 && !memoryWarningRef.current) {
          console.warn('High memory usage detected, clearing caches...');
          memoryWarningRef.current = true;
          
          // Clear all non-essential cache
          queryClient.removeQueries({ 
            predicate: (query) => {
              const key = query.queryKey[0] as string;
              return key?.includes('book-media') || 
                     key?.includes('covers') || 
                     key?.includes('images');
            }
          });
          
          // Reset warning after 30 seconds
          setTimeout(() => {
            memoryWarningRef.current = false;
          }, 30000);
        }
      }
    };

    const memoryInterval = setInterval(checkMemoryPressure, 10000); // Check every 10 seconds

    return () => clearInterval(memoryInterval);
  }, [queryClient]);

  return { isAppVisible };
};
